import { useSyncExternalStore } from 'react'
import { useApp } from '@/store/app'
import { useMediaQuery } from '@/lib/useMediaQuery'

/**
 * DRK-Logo mit Gliederungszusatz.
 *
 * Aufbau nach Styleguide: Das Kompaktlogo (Rotkreuz-Zeichen und Wortmarke) bleibt das
 * Hauptelement, die Organisationseinheit steht abgesetzt daneben — sie ersetzt die
 * Wortmarke ausdrücklich nicht.
 *
 * Die Logosystematik setzt die Einheit unter das Logo, getrennt durch eine waagerechte
 * Linie. Für eine Kopfleiste, die auf jedem Bildschirm mitläuft, ist das zu hoch: Drei
 * Zeilen unter dem Logo kosten rund 85 Pixel, nebeneinander sind es 55. Deshalb steht
 * die Einheit neben dem Logo, getrennt durch eine senkrechte Linie — dieselbe Aussage
 * im Querformat.
 *
 * Auf schmalen Displays reicht die Breite dafür nicht (nebeneinander misst der Block
 * rund 370 Pixel). Dort greift die gestapelte Anordnung der Logosystematik, also
 * Einheit unter dem Logo mit waagerechter Linie.
 *
 * ACHTUNG — maßstabsgetreuer Nachbau, keine offizielle Logodatei. Das Rotkreuz-Zeichen
 * ist geometrisch eindeutig definiert (griechisches Kreuz aus fünf gleichen Quadraten),
 * die Wortmarke steht in der Hausschrift. Für die Veröffentlichung sind die Dateien aus
 * dem Styleguide einzusetzen (Basiselemente → Logo); die genauen Abstände und Größen des
 * Gliederungszusatzes stehen im PDF „Logosystematik Organisationseinheit".
 *
 * Das Rotkreuz-Zeichen ist nach den Genfer Abkommen geschützt und darf nur von
 * Berechtigten geführt werden.
 */

export type LogoVariant = 'standard' | 'weiss'

/**
 * Bezeichnung der Organisationseinheit, zeilenweise.
 *
 * Der Umbruch steht hier bewusst fest und wird nicht dem Zufall der Spaltenbreite
 * überlassen: Ein Verbandsname, der mitten in „Kreisverband" umbricht, liest sich
 * falsch. Für eine andere Gliederung genügt es, diese Zeilen zu ändern.
 */
export const ORGANISATIONSEINHEIT = [
  'DRK Kreisverband',
  'Hamburg Eimsbüttel e.V.',
  'Bereitschaften',
] as const

export function Logo({
  variant = 'standard',
  className = '',
  height = 32,
  einheit = ORGANISATIONSEINHEIT,
}: {
  variant?: LogoVariant
  className?: string
  /** Höhe des Rotkreuz-Zeichens in Pixeln; alle übrigen Maße leiten sich davon ab. */
  height?: number
  /** Zeilen des Gliederungszusatzes; leer lässt ihn weg. */
  einheit?: readonly string[]
}) {
  // Balkenbreite = ein Drittel des Kreuzes. Sie bestimmt zugleich den Schutzraum.
  // Auf ganze Pixel gerundet: Fällt eine Kante auf einen halben Pixel, glättet der
  // Browser sie weich — das Kreuz wirkt dann unscharf statt gestochen.
  const cross = Math.round(height / 3) * 3
  const bar = cross / 3

  const rot = variant === 'weiss' ? '#ffffff' : '#e60005'
  const schrift = variant === 'weiss' ? '#ffffff' : '#000000'
  const linie = variant === 'weiss' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.25)'

  // Ganzzahlige Grade und Zeilenhöhen: Bruchteilpositionen zeichnet der Browser weich.
  const marke = Math.round(bar * 1.0)
  const zusatz = Math.max(10, Math.round(bar * 0.95))
  const zeile = zusatz + 3

  // Höhe des Textblocks — bestimmt zugleich die Länge der senkrechten Trennlinie.
  const blockHoehe = einheit.length * zeile
  // Der Schwellwert ist gemessen — und zwar mit Open Sans, der breiter laufenden
  // Ersatzschrift: Was damit passt, passt mit Helvetica Neue erst recht.
  const nebeneinander = useMediaQuery('(min-width: 384px)')

  return (
    <div
      className={`flex ${nebeneinander ? 'items-center' : 'flex-col items-start'} ${className}`}
      style={{ gap: nebeneinander ? bar : 0, padding: bar }}
      role="img"
      aria-label={`Deutsches Rotes Kreuz — ${einheit.join(' ')}`}
    >
      <svg
        viewBox="0 0 3 3"
        width={cross}
        height={cross}
        aria-hidden
        // Das Kreuz besteht nur aus achsenparallelen Kanten; ohne Glättung bleiben
        // sie hart, wie es dem Schutzzeichen entspricht.
        shapeRendering="crispEdges"
        style={{ flexShrink: 0 }}
      >
        {/* Griechisches Kreuz aus fünf gleichen Quadraten — die verbindliche Form
            des Schutzzeichens. */}
        <path d="M1 0h1v1h1v1H2v1H1V2H0V1h1z" fill={rot} />
      </svg>

      <div
        aria-hidden
        style={{
          fontSize: marke,
          lineHeight: `${cross}px`,
          color: schrift,
          // Gewicht 700 statt 600: Helvetica Neue hat keinen halbfetten Schnitt,
          // und ein errechnetes Fett sieht matschig aus.
          fontWeight: 700,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Deutsches Rotes Kreuz
      </div>

      {einheit.length > 0 && (
        <>
          {/* Die Trennlinie steht quer zur Leserichtung des Blocks: senkrecht, wenn
              die Einheit neben dem Logo steht, waagerecht, wenn sie darunter steht. */}
          <div
            aria-hidden
            style={
              nebeneinander
                ? {
                    width: 1,
                    height: blockHoehe,
                    background: linie,
                    flexShrink: 0,
                    // auto schiebt den freien Platz vor die Linie: Linie und Einheit
                    // rücken gemeinsam an den rechten Rand, das Logo bleibt links.
                    // Der Abstand des Flex-Containers bleibt als Mindestmaß erhalten,
                    // sodass auf schmalen Displays nichts zusammenklebt.
                    marginLeft: 'auto',
                    marginRight: bar * 1.4,
                  }
                : { height: 1, width: '100%', background: linie, marginTop: 2, marginBottom: 4 }
            }
          />
          <div aria-hidden style={{ minWidth: 0 }}>
            {einheit.map((text) => (
              <div
                key={text}
                style={{
                  fontSize: zusatz,
                  lineHeight: `${zeile}px`,
                  color: schrift,
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Wählt die Logovariante passend zum Erscheinungsbild. Auf dunklem Grund ist die
 * weiße Version vorgeschrieben — schwarze Wortmarke auf Dunkelblau wäre unlesbar.
 */
export function useLogoVariant(): LogoVariant {
  const theme = useApp((s) => s.theme)
  const systemDark = useSystemDark()
  if (theme === 'hell') return 'standard'
  if (theme === 'nacht' || theme === 'rot') return 'weiss'
  return systemDark ? 'weiss' : 'standard'
}

function useSystemDark(): boolean {
  return useSyncExternalStore(
    (notify) => {
      const list = window.matchMedia('(prefers-color-scheme: dark)')
      list.addEventListener('change', notify)
      return () => list.removeEventListener('change', notify)
    },
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
    () => false,
  )
}
